import React from 'react';
import { Button, Chip } from '@heroui/react';
import { Typography } from '../../../../components';
import { PartnerOrderActionType, PartnerOrderData } from '../types';
import { getStatusLabel } from '../utils';
import { useTranslations } from '../../../../contexts/translation.context';

interface StatusCellProps {
  order: PartnerOrderData;
  onOrderAction: (order: PartnerOrderData, actionType: PartnerOrderActionType) => void;
  actionOrderId: string | null;
}

const StatusCell: React.FC<StatusCellProps> = ({ order, onOrderAction, actionOrderId }) => {
  const { t } = useTranslations();
  const isActionLoading = actionOrderId === order.id;
  const showAccept = order.status === 'waiting';
  const showCancel = order.status === 'in_progress' || order.status === 'waiting';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'bg-blue-600';
      case 'waiting':
        return 'bg-yellow-600';
      case 'completed':
        return 'bg-green-600';
      case 'canceled':
        return 'bg-red-600';
      case 'not_started':
        return 'bg-auth-muted';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <div className="flex min-w-[8.5rem] flex-col items-start gap-2">
      <Chip
        className="rounded-lg bg-gray-100 text-gray-900"
        classNames={{
          base: 'h-6 px-2 py-1 gap-1',
        }}
        size="sm"
        startContent={
          <div className={`h-2 w-2 rounded-full ${getStatusColor(order.status)}`}></div>
        }
      >
        <Typography variant="body3" className="text-gray-900">
          {getStatusLabel(order.status, t)}
        </Typography>
      </Chip>

      {(showAccept || showCancel) && (
        <div className="flex flex-col gap-1.5">
          {showAccept && (
            <Button
              size="sm"
              radius="sm"
              variant="flat"
              className="h-7 min-w-0 bg-partner/10 px-2.5 font-medium text-partner"
              isLoading={isActionLoading}
              isDisabled={isActionLoading}
              onPress={() => onOrderAction(order, 'accept')}
            >
              {t('partner.orders.actions.accept')}
            </Button>
          )}
          {showCancel && (
            <Button
              size="sm"
              radius="sm"
              variant="bordered"
              className="h-7 min-w-0 border-auth-strength-none px-2.5 font-medium text-auth-ink"
              isLoading={isActionLoading}
              isDisabled={isActionLoading}
              onPress={() => onOrderAction(order, 'cancel')}
            >
              {t('partner.orders.actions.cancel')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default StatusCell;
